import React from "react";
import { Helmet } from "react-helmet";
import { Check, MapPin, Wrench } from "lucide-react";
import { Eyebrow } from "./ui/Eyebrow";
import { PrimaryLink } from "./ui/Buttons";
import { ServiceCardLink } from "./ui/ServiceCard";
import CallButton from "./ui/CallButton";
import DirectionsButton from "./ui/DirectionsButton";
import BusinessHours from "./ui/BusinessHours";

/* service photos */
import ABS from "../assets/ABS.png";
import ALLF from "../assets/AllFactory.png";
import ALT from "../assets/Alternator.png";
import Battery from "../assets/battery.jpg";
import Brake from "../assets/BrakeF.jpg";
import Coolant from "../assets/coolant.jpg";
import Differ from "../assets/Differential.png";
import ElectD from "../assets/ElectricalDiag.png";
import EngineF from "../assets/EngineFlush.png";
import Fuel from "../assets/Fuel.png";
import Oil from "../assets/OilChange.jpg";
import Parasitic from "../assets/Parasitic.png";
import Power from "../assets/Power.png";
import Radiator from "../assets/Radiator.png";
import SmokeT from "../assets/SmokeTest.jpg";
import Timing from "../assets/TimingBelt.jpg";
import TransferC from "../assets/TransferCase.png";
import TransF from "../assets/TransmissionFlush.png";
import TuneUp from "../assets/TuneUps.png";
import WaterP from "../assets/WaterP.png";
import European from "../assets/European.png";
import LandRover from "../assets/LandRover.png";
import Jaguar from "../assets/Jaguar.png";
import Smog from "../assets/smogT.png";
import Suspension from "../assets/Suspension.png";
import AC from "../assets/AC.jpg";
import Align from "../assets/align.png";

/** Top-level SEO landing pages — featured before the full service catalog. */
const specializedRepairServices = [
  {
    title: "Auto Repair",
    description:
      "Diagnostics, maintenance, and repairs for daily drivers and imports.",
    to: "/auto-repair",
    image: ALLF,
  },
  {
    title: "Check Engine Light",
    description:
      "Accurate scans and testing when your dash warns of a problem.",
    to: "/check-engine-light",
    image: ElectD,
  },
  {
    title: "Transmission Repair",
    description:
      "Shifting issues, fluid service, and drivetrain-related care.",
    to: "/transmission-repair",
    image: TransF,
  },
  {
    title: "European Auto Repair",
    description:
      "Maintenance and repair for BMW, Mercedes, Audi, and other imports.",
    to: "/european-auto-repair",
    image: European,
  },
  {
    title: "Land Rover Repair",
    description:
      "SUV service, 4x4 systems, and smog for Land Rover and Range Rover vehicles.",
    to: "/land-rover-repair",
    image: LandRover,
  },
  {
    title: "Jaguar Repair",
    description:
      "Diagnostics, maintenance, and repair for Jaguar vehicles.",
    to: "/jaguar-repair",
    image: Jaguar,
  },
];

const introChipClass =
  "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-700 md:text-xs";

const introChipIconWrap =
  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-600/12 text-teal-700";

const serviceDetails = [
  {
    title: "Smog & Emissions",
    description:
      "California smog checks, emissions testing, and repair guidance when a vehicle does not pass.",
    link: "/services/smog-emissions-services",
    image: Smog,
  },
  {
    title: "Brakes & Suspension",
    description:
      "Brake service, shocks, struts, and suspension repairs for safer stopping and a smoother ride.",
    link: "/services/brake-suspension",
    image: Suspension,
  },
  {
    title: "Air Conditioning",
    description:
      "A/C inspections, refrigerant service, and leak checks to keep the cabin comfortable.",
    link: "/services/air-conditioning",
    image: AC,
  },
  {
    title: "Tires & Alignment",
    description:
      "Tire support, rotations, balancing, and alignment-related service for even wear and smoother driving.",
    link: "/services/tire-alignment",
    image: Align,
  },
  {
    title: "Transfer Case",
    description:
      "Transfer case repairs, maintenance, and fluid services for smooth drivetrain performance.",
    link: "/services/transfer-case",
    image: TransferC,
  },
  {
    title: "Differential Service",
    description:
      "Differential repairs, maintenance, and fluid changes for enhanced drivetrain efficiency.",
    link: "/services/diff-service",
    image: Differ,
  },
  {
    title: "Oil Change",
    description:
      "Quick and reliable oil changes to keep your engine running smoothly.",
    link: "/services/oil-change",
    image: Oil,
  },
  {
    title: "Battery Replacement",
    description:
      "Battery testing and replacement so your car starts every time.",
    link: "/services/battery-replacement",
    image: Battery,
  },
  {
    title: "Smoke Test",
    description:
      "Smoke testing to detect leaks and keep your emissions system airtight.",
    link: "/services/smoke-test",
    image: SmokeT,
  },
  {
    title: "Transmission Flush",
    description:
      "Transmission flushes to maintain smooth shifting and long-term reliability.",
    link: "/services/transmission-flush",
    image: TransF,
  },
  {
    title: "All Factory Maintenance",
    description:
      "Factory-recommended maintenance to keep your warranty and your car healthy.",
    link: "/services/all-factory",
    image: ALLF,
  },
  {
    title: "ABS System",
    description:
      "ABS diagnostics and repairs so your braking system stays safe and responsive.",
    link: "/services/abs-system",
    image: ABS,
  },
  {
    title: "Timing Belt Replacement",
    description:
      "Prevent engine damage with on-time timing belt replacement.",
    link: "/services/timing-belt-replacement",
    image: Timing,
  },
  {
    title: "Tune Ups",
    description:
      "Full tune-ups to restore power, efficiency, and smooth driving.",
    link: "/services/tune-ups",
    image: TuneUp,
  },
  {
    title: "Parasitic Draw",
    description:
      "Find and fix hidden electrical draws that drain your battery overnight.",
    link: "/services/parasitic-draw",
    image: Parasitic,
  },
  {
    title: "Alternator Replacement",
    description:
      "Charging system inspections and alternator replacements done right.",
    link: "/services/alternator-replacement",
    image: ALT,
  },
  {
    title: "Radiator Replacement",
    description:
      "Radiator repairs and replacements to prevent overheating.",
    link: "/services/radiator-replacement",
    image: Radiator,
  },
  {
    title: "Water Pump",
    description:
      "Water pump service to keep coolant flowing and temps under control.",
    link: "/services/water-pump",
    image: WaterP,
  },
  {
    title: "Power Steering Flush",
    description:
      "Power steering fluid flushes for smoother, quieter steering.",
    link: "/services/power-steering-flush",
    image: Power,
  },
  {
    title: "Engine Flush",
    description:
      "Engine flushes to remove sludge and help your engine run cleaner.",
    link: "/services/engine-flush",
    image: EngineF,
  },
  {
    title: "Fuel Injection Service",
    description:
      "Fuel injector cleaning for better throttle response and mileage.",
    link: "/services/fuel-injection",
    image: Fuel,
  },
  {
    title: "Brake Flush",
    description:
      "Brake fluid flushes to keep pedal feel firm and braking performance strong.",
    link: "/services/brake-flush",
    image: Brake,
  },
  {
    title: "Coolant Flush",
    description:
      "Coolant flushes to protect your engine from overheating and corrosion.",
    link: "/services/coolant-flush",
    image: Coolant,
  },
];

const Services = () => {
  return (
    <>
      <Helmet>
        <title>
          Smog Check & Auto Repair Services in Temecula | Advanced Smog
        </title>
        <meta
          name="description"
          content="Need a smog check or auto repair in Temecula? We offer emissions testing, oil changes, diagnostics, and maintenance for all vehicles, including high-end cars. Visit Advanced Smog today!"
        />
        <meta
          name="keywords"
          content="smog check near me, auto repair Temecula, smog test, car diagnostics, emissions test, luxury car maintenance, oil change, brake service, transmission repair, high-end vehicle service, Temecula auto repair shop"
        />
        <meta
          property="og:title"
          content="Smog Check & Auto Repair Services in Temecula | Advanced Smog"
        />
        <meta
          property="og:description"
          content="Need a smog check or auto repair in Temecula? We offer emissions testing, oil changes, diagnostics, and maintenance for all vehicles, including high-end cars. Visit Advanced Smog today!"
        />
        <meta
          property="og:image"
          content="https://www.advancedautomotivesmog.com/assets/services.jpeg"
        />
        <meta
          property="og:url"
          content="https://www.advancedautomotivesmog.com/services"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Smog Check & Auto Repair Services in Temecula | Advanced Smog"
        />
        <meta
          name="twitter:description"
          content="Fast and reliable smog check and auto repair services for all vehicles, including high-end models. Visit Advanced Smog in Temecula today!"
        />
        <meta
          name="twitter:image"
          content="https://www.advancedautomotivesmog.com/assets/services.jpeg"
        />
        <link
          rel="canonical"
          href="https://www.advancedautomotivesmog.com/services"
        />
      </Helmet>

      <div className="min-h-screen bg-[#F8F9FA] pb-12 pt-7 text-slate-700 md:pb-16 md:pt-8">
        {/* Hero / intro */}
        <section className="border-b border-slate-200 bg-[#EEF2F7] py-10 md:py-12">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            <Eyebrow variant="compact" tone="light" className="mb-3 md:mb-4">
              <>
                <span className="md:hidden">Service Overview</span>
                <span className="hidden md:inline">
                  Advanced Automotive · Services
                </span>
              </>
            </Eyebrow>

            <h1 className="max-w-4xl text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-2xl md:text-4xl lg:text-[2.65rem]">
              <span className="md:hidden">
                What we can{" "}
                <span className="text-teal-600">take care of</span> for your car.
              </span>
              <span className="hidden md:inline">
                Every service your car needs,{" "}
                <span className="text-teal-600">in one trusted shop.</span>
              </span>
            </h1>

            <p className="mt-4 max-w-3xl text-sm font-normal leading-relaxed text-slate-700 md:text-base">
              From basic maintenance to complex diagnostics, drivetrain, and cooling system
              work—we keep daily drivers, European vehicles, and EVs on the road safely and
              reliably.
            </p>
            <p className="mt-2 text-xs text-slate-500 md:hidden">
              Scroll to browse everything we offer. Tap any item for details.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className={introChipClass}>
                <span className={introChipIconWrap} aria-hidden>
                  <Check className="h-3 w-3" strokeWidth={2.75} />
                </span>
                Complete car care
              </span>
              <span className={introChipClass}>
                <span className={introChipIconWrap} aria-hidden>
                  <Wrench className="h-3 w-3" strokeWidth={2.25} />
                </span>
                High-end &amp; EV friendly
              </span>
              <span className={introChipClass}>
                <span className={introChipIconWrap} aria-hidden>
                  <MapPin className="h-3 w-3" strokeWidth={2.25} />
                </span>
                Temecula, CA
              </span>
            </div>
          </div>
        </section>

        {/* Specialized Repair Services */}
        <section className="border-t border-b border-slate-200 bg-[#F8F9FA] py-10 md:py-14">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Specialized Repair Services
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700 md:text-[15px]">
              High-value service pages for common repairs, diagnostics, and import
              vehicles—plus dedicated Land Rover and Jaguar care. Start here if you want
              a focused landing page before browsing the full catalog.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {specializedRepairServices.map((item) => (
                <ServiceCardLink
                  key={item.to}
                  to={item.to}
                  className="group flex flex-col overflow-hidden p-0"
                >
                  <div className="relative h-32 w-full overflow-hidden md:h-36">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                      style={{ backgroundImage: `url(${item.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col justify-between bg-white p-4">
                    <div>
                      <h3 className="mb-1 text-base font-semibold text-navy-900 md:text-[1.05rem]">
                        {item.title}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-600 md:text-[0.8rem]">
                        {item.description}
                      </p>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[0.75rem] font-medium text-teal-600">
                      <span className="transition-colors duration-300 group-hover:text-teal-700">
                        Learn more
                      </span>
                      <span className="text-sm text-slate-400 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-teal-600 motion-reduce:group-hover:translate-x-0">
                        ›
                      </span>
                    </div>
                  </div>
                </ServiceCardLink>
              ))}
            </div>
          </div>
        </section>

        {/* Full service catalog */}
        <section className="border-t border-b border-slate-200 bg-[#EEF2F7] py-10 md:py-14">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            <h2 className="text-xl font-semibold tracking-tight text-navy-900 sm:text-2xl">
              Full service catalog
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700 md:text-[15px]">
              Maintenance, diagnostics, drivetrain, cooling, and more—everything in one
              trusted Temecula shop.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 md:gap-6 lg:grid-cols-3">
              {serviceDetails.map((service, idx) => (
                <ServiceCardLink
                  key={idx}
                  to={service.link}
                  className="group flex flex-col overflow-hidden p-0"
                >
                  <div className="relative h-40 w-full overflow-hidden md:h-44">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                      style={{ backgroundImage: `url(${service.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                  </div>

                  <div className="flex flex-1 flex-col justify-between bg-white p-4">
                    <div>
                      <h3 className="mb-1 text-base font-semibold text-navy-900 md:text-lg">
                        {service.title}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-600 md:text-sm">
                        {service.description}
                      </p>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[0.75rem] font-medium text-teal-600">
                      <span className="transition-colors duration-300 group-hover:text-teal-700">
                        Learn more
                      </span>
                      <span className="text-sm text-slate-400 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-teal-600 motion-reduce:group-hover:translate-x-0">
                        ›
                      </span>
                    </div>
                  </div>
                </ServiceCardLink>
              ))}
            </div>
          </div>
        </section>

        {/* Quick actions + hours */}
        <section className="border-t border-slate-200 bg-white py-10 md:py-14">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
              Visit or call the shop
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Not sure which service you need? Tell us what your car is doing—we&apos;ll
              point you in the right direction.
            </p>

            <BusinessHours className="mt-6 max-w-md" tone="light" />

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <CallButton className="w-full justify-center px-6 py-3 text-sm sm:w-auto sm:min-w-[10rem]" />
              <DirectionsButton className="w-full justify-center px-6 py-3 text-sm sm:w-auto sm:min-w-[10rem]" />
            </div>

            <div className="mt-8 border-t border-slate-200 pt-8 text-center sm:text-left">
              <PrimaryLink
                to="/contact"
                className="inline-flex px-8 py-3 text-sm md:text-base"
              >
                Contact us about a specific issue
              </PrimaryLink>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;

















